import { TFunction } from "i18next";
import * as Yup from "yup";

import { AssetsMaxLength } from "../../../asset-inventory/views/assets/AssetsView.types";
import { ClaimStatusKey } from "../../claimStatus";
import {
  BalanceEntryFormValues,
  ClaimCollateralFormValues,
  ClaimFormValues,
  ClaimMaxLength,
  ExpectedLossPercentRange,
} from "./ClaimFormFields.types";
import { isHoldingCollateralType } from "./components/collaterals/Collaterals.utils";

/**
 * A blank balance history (Saldo-Historie) row. The date is prefilled with today because that is the
 * date the entry is being recorded for in practice; every other field is left to the user.
 */
export const createEmptyBalanceEntry = (): BalanceEntryFormValues => ({
  id: undefined,
  date: new Date(),
  amount: null,
  entryType: null,
});

// Takes only the field it reads: Yup infers the element type from its own shape, which is narrower
// than BalanceEntryFormValues.
function registeredEntryIndexes(
  entries: ReadonlyArray<{ entryType?: string | null }> | undefined,
  isSrvRegisteredEntryType: (entryType: string | null | undefined) => boolean
): number[] {
  return (entries ?? []).flatMap((entry, index) => (isSrvRegisteredEntryType(entry.entryType) ? [index] : []));
}

function expectedLossRangeMessage(t: TFunction): string {
  return t("claims.validation.expectedLossRange", {
    min: ExpectedLossPercentRange.min,
    max: ExpectedLossPercentRange.max,
  });
}

function maxLength(t: TFunction, max: number) {
  return Yup.string().nullable().max(max, t("common.validation.maxLength", { max }));
}

/** A security block: the type is always required, the asset only for the types that refer to one. */
function collateralSchema(t: TFunction) {
  return Yup.object().shape({
    type: Yup.string()
      .nullable()
      .required(t("common.validation.fieldRequired", { fieldName: t("claims.collaterals.type") })),
    holdingSelection: Yup.string()
      .nullable()
      .test("holding-required", function (value) {
        const { type } = this.parent as ClaimCollateralFormValues;
        if (!isHoldingCollateralType(type) || value) {
          return true;
        }
        return this.createError({
          message: t("common.validation.fieldRequired", {
            fieldName: t(`claims.collaterals.holdingQuestion.${type}`),
          }),
        });
      }),
    details: maxLength(t, ClaimMaxLength.collateralDetails),
    newHolding: Yup.object().shape({
      institutionName: maxLength(t, AssetsMaxLength.institutionName),
      accountNumber: maxLength(t, AssetsMaxLength.accountNumber),
      street: maxLength(t, AssetsMaxLength.street),
      houseNumber: maxLength(t, AssetsMaxLength.houseNumber),
      zipCode: maxLength(t, AssetsMaxLength.zipCode),
      city: maxLength(t, AssetsMaxLength.city),
      remarks: maxLength(t, AssetsMaxLength.remarks),
    }),
  });
}

/**
 * @param isSrvRegisteredEntryType which balance-history types record the amount filed in the SRV.
 * Read from the types' SrvRegistered parameter rather than assumed, so renaming the shipped option
 * leaves both rules below intact — the backend decides the same way.
 */
export function claimValidationSchema(
  t: TFunction,
  isSrvRegisteredEntryType: (entryType: string | null | undefined) => boolean,
  isEdit = false
) {
  return Yup.object().shape({
    ...(isEdit
      ? {}
      : {
          creditorId: Yup.string().required(
            t("common.validation.fieldRequired", { fieldName: t("claims.form.creditor") })
          ),
        }),
    caseNumber: Yup.string().max(
      ClaimMaxLength.caseNumber,
      t("common.validation.maxLength", { max: ClaimMaxLength.caseNumber })
    ),
    legalBasis: Yup.string().required(t("common.validation.fieldRequired", { fieldName: t("claims.form.legalBasis") })),
    disputedStatus: Yup.string().required(
      t("common.validation.fieldRequired", { fieldName: t("claims.form.disputedStatus") })
    ),
    collateralStatus: Yup.string().required(
      t("common.validation.fieldRequired", { fieldName: t("claims.form.collateralStatus") })
    ),
    claimEntirelyDisputed: Yup.string().when("disputedStatus", {
      is: ClaimStatusKey.Yes,
      then: (schema) =>
        schema.required(t("common.validation.fieldRequired", { fieldName: t("claims.form.claimEntirelyDisputed") })),
      otherwise: (schema) => schema.optional(),
    }),
    recognizedAmount: Yup.number()
      .nullable()
      .when(["disputedStatus", "claimEntirelyDisputed"], {
        is: (disputedStatus: string, claimEntirelyDisputed: string) =>
          disputedStatus === ClaimStatusKey.Yes && claimEntirelyDisputed === ClaimStatusKey.No,
        then: (schema) =>
          schema.required(t("common.validation.fieldRequired", { fieldName: t("claims.form.recognizedAmount") })),
        otherwise: (schema) => schema.optional(),
      }),
    // A claim registered in the SRV records the filed amount as exactly one "angemeldet" entry. The
    // reverse is deliberately not checked: such an entry is allowed while the field is empty or Nein.
    // The missing entry is this field's problem, so the message belongs on it rather than on the list.
    srvRegistrationStatus: Yup.string().test(
      "srv-requires-registered-entry",
      t("claims.validation.srvRequiresRegisteredEntry"),
      function (value) {
        if (value !== ClaimStatusKey.Yes) {
          return true;
        }

        const { balanceEntries } = this.parent as ClaimFormValues;
        return registeredEntryIndexes(balanceEntries, isSrvRegisteredEntryType).length > 0;
      }
    ),
    expectedLossPercent: Yup.number()
      .nullable()
      .when("collateralStatus", {
        is: ClaimStatusKey.Yes,
        then: (schema) =>
          schema
            .required(t("common.validation.fieldRequired", { fieldName: t("claims.collaterals.expectedLoss") }))
            .min(ExpectedLossPercentRange.min, expectedLossRangeMessage(t))
            .max(ExpectedLossPercentRange.max, expectedLossRangeMessage(t)),
        otherwise: (schema) => schema.optional(),
      }),
    collaterals: Yup.array().when("collateralStatus", {
      is: ClaimStatusKey.Yes,
      then: (schema) => schema.of(collateralSchema(t)),
      otherwise: (schema) => schema.optional(),
    }),
    balanceEntries: Yup.array()
      .of(
        Yup.object().shape({
          date: Yup.date()
            .nullable()
            .required(t("common.validation.fieldRequired", { fieldName: t("claims.balanceHistory.date") })),
          amount: Yup.number()
            .nullable()
            .required(t("common.validation.fieldRequired", { fieldName: t("claims.balanceHistory.amount") })),
          entryType: Yup.string().nullable(),
        })
      )
      // Reported on the second registered entry's Typ rather than on the list: that is the row the user
      // has to change, and an error on a real field is what marks an input invalid — which is how the
      // dialog highlights it and how "Zum ersten Fehler" finds it.
      .test("single-registered-entry", t("claims.validation.singleRegisteredEntry"), function (entries) {
        const [, secondIndex] = registeredEntryIndexes(entries, isSrvRegisteredEntryType);
        return secondIndex === undefined || this.createError({ path: `${this.path}[${secondIndex}].entryType` });
      }),
  });
}
